import { useEffect, useState } from "react";
import { GitCompareArrows, Plus, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { API, errMsg } from "../../lib/api";
import { useAuth, STAFF_ROLES } from "../../context/AuthContext";
import { PageHeader, Loading, Money, StatusBadge, Modal, Field, ErrBox, Empty } from "../../components/common";

export default function Reconciliation() {
  const { user } = useAuth();
  const [recs, setRecs] = useState(null);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ period_label: "", merchant_total: "", bank_deposits: "", manual: "", cash: "", accounting: "", notes: "" });
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  const load = () => API.get("/reconciliation").then((r) => setRecs(r.data.reconciliations));
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault(); setBusy(true); setErr("");
    try {
      await API.post("/reconciliation", form);
      toast.success("Reconciliation recorded");
      setOpen(false); load();
    } catch (e2) { setErr(errMsg(e2)); }
    setBusy(false);
  };

  const approve = async (id) => {
    try {
      await API.post(`/reconciliation/${id}/approve`, {});
      toast.success("Reconciliation approved");
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const canApprove = user && (STAFF_ROLES.includes(user.role) || user.role === "BUSINESS_OWNER");

  if (!recs) return <Loading label="Loading reconciliation" />;

  return (
    <div data-testid="reconciliation-page">
      <PageHeader title="Merchant Reconciliation" sub="Merchant totals vs. bank deposits vs. reported sales — discrepancies flagged."
        right={<button className="btn-navy" onClick={() => setOpen(true)} data-testid="new-reconciliation-btn"><Plus className="h-4 w-4" /> New Reconciliation</button>} />

      {recs.length === 0 ? (
        <Empty title="No reconciliations yet" msg="Record your first period comparison to surface deposit discrepancies." />
      ) : (
        <div className="grid gap-4 lg:grid-cols-2">
          {recs.map((r) => (
            <div key={r.id} className="card-gov p-5" data-testid={`recon-${r.period_label}`}>
              <div className="flex items-center justify-between">
                <h3 className="font-bold text-navy">{r.period_label || "Period"}</h3>
                <StatusBadge status={r.status} />
              </div>
              <dl className="mt-4 grid grid-cols-2 gap-3 text-sm">
                {[["Merchant Sales", r.merchant_total_cents], ["Bank Deposits", r.bank_deposits_cents],
                  ["Manual Sales", r.manual_cents], ["Cash Sales", r.cash_cents],
                  ["Accounting Totals", r.accounting_cents]].map(([k, v]) => (
                  <div key={k} className="rounded border border-slate-200 px-3 py-2">
                    <dt className="text-[10px] uppercase tracking-widest text-slate-400">{k}</dt>
                    <dd className="money font-bold text-slate-800"><Money cents={v} /></dd>
                  </div>
                ))}
                <div className={`rounded border px-3 py-2 ${r.difference_cents !== 0 ? "border-rose-200 bg-rose-50" : "border-emerald-200 bg-emerald-50"}`}>
                  <dt className="text-[10px] uppercase tracking-widest text-slate-400">Difference</dt>
                  <dd className={`money font-bold ${r.difference_cents !== 0 ? "text-rose-700" : "text-emerald-700"}`}><Money cents={r.difference_cents} /></dd>
                </div>
              </dl>
              {r.notes && <p className="mt-3 text-xs text-slate-500">Notes: {r.notes}</p>}
              <div className="mt-4 flex items-center justify-between">
                <span className="text-xs text-slate-400">{r.approved_by ? `Approved by ${r.approved_by}` : "Not approved"}</span>
                {canApprove && r.status !== "APPROVED" && (
                  <button className="btn-outline-gov px-3 py-1.5 text-xs" onClick={() => approve(r.id)} data-testid={`approve-recon-${r.id}`}>
                    <CheckCircle2 className="h-3.5 w-3.5" /> Approve
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      <Modal open={open} onClose={() => setOpen(false)} title="New Reconciliation">
        <form onSubmit={submit} className="space-y-4" data-testid="recon-form">
          <Field label="Period (e.g. 2026-05)"><input className="input-gov" value={form.period_label} onChange={set("period_label")} required data-testid="recon-period" /></Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Merchant Totals ($)"><input className="input-gov money" inputMode="decimal" value={form.merchant_total} onChange={set("merchant_total")} required data-testid="recon-merchant" /></Field>
            <Field label="Bank Deposits ($)"><input className="input-gov money" inputMode="decimal" value={form.bank_deposits} onChange={set("bank_deposits")} required data-testid="recon-bank" /></Field>
            <Field label="Manual Sales ($)"><input className="input-gov money" inputMode="decimal" value={form.manual} onChange={set("manual")} data-testid="recon-manual" /></Field>
            <Field label="Cash Sales ($)"><input className="input-gov money" inputMode="decimal" value={form.cash} onChange={set("cash")} data-testid="recon-cash" /></Field>
            <Field label="Accounting Totals ($)"><input className="input-gov money" inputMode="decimal" value={form.accounting} onChange={set("accounting")} data-testid="recon-accounting" /></Field>
          </div>
          <Field label="Notes"><textarea className="input-gov" rows={2} value={form.notes} onChange={set("notes")} data-testid="recon-notes" /></Field>
          <ErrBox msg={err} />
          <button className="btn-navy w-full" disabled={busy} data-testid="recon-submit">{busy ? "Saving…" : "Save Reconciliation"}</button>
        </form>
      </Modal>
    </div>
  );
}
